import { Component, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { VendorService } from 'src/app/services/Vendor.service';
import { ANGULAR_MODULES, SYNCFUSION_MODULES } from 'src/app/Shared/shared-imports';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { LoaderComponent } from "src/app/Shared/loader/loader.component";
import { PhoneFormatPipe } from 'src/app/Shared/phone-format.pipe';
import { GeneralService } from 'src/app/services/general.service';
import { MaskedTextBoxModule } from '@syncfusion/ej2-angular-inputs';
import { DatePipe } from '@angular/common';
import { PercentageFormatterDirective } from 'src/app/Shared/percentageFormatter.directive';
import { PosSettingsService } from 'src/app/services/pos-settings.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-ops-vendor-settings',
  imports: [ANGULAR_MODULES, SYNCFUSION_MODULES, LoaderComponent, PhoneFormatPipe, MaskedTextBoxModule, PercentageFormatterDirective],
  providers: [DatePipe],
  standalone: true,
  templateUrl: './ops-vendor-settings.html',
  styleUrl: './ops-vendor-settings.scss'
})
export class OpsVendorSettings {

  loaderIcon: Promise<boolean>;
  private subscriptions: Subscription[] = [];
  vendorSettingsForm: FormGroup;
  @Input() vendorId;
  response;
  vendorInfo;
  vendorMarketID = "";
  vendorName = "";
  cellPhone = "";
  telephone = "";
  Email = "";
  Address = "";
  City = "";
  statesList: any[] = [];
  OverrideCommissions = false;
  public StateDropdownFields: Object = {
    text: 'StateName',
    value: 'StateCode'
  };
  public SSNMask: String = '000-00-0000';

  originalFlatCommission = 0;
  hasTransactionsAfterLastPayout = false;
  enableInventoryControl = false;
  hasInventoryTransactions = false;
  defaultFlatCommission: number = 0;
  defaultConCommission: number = 0;

  constructor(private vendorService: VendorService,
    private toastr: ToastrService,
    private generalService: GeneralService,
    private sharedHTMLSettings: SharedHTMLSettingsDataService,
    private formBuilder: FormBuilder,
    private datePipe: DatePipe,
    private posSettingsService: PosSettingsService
  ) {
    this.vendorSettingsForm = this.formBuilder.group(
      {
        VendorId: [0, [Validators.required]],
        MarketVendorId: ['0'],
        ExcludeAutoMonthlyRenewal: [false],
        AutoDeductRent: [false],
        RefundPosSalesTax: [false],
        DisablePrintSettlementCheckForDealer: [false],
        ConsignmentVendor: [false],
        ApplyFlatCommissionPos: [false],
        EnableConsignmentCommission: [false],
        FlatCommissionPos: [''],
        ConsignmentCommission: [''],
        VendorNameOnCheck: [''],
        VendorCityOnCheck: [''],
        VendorStateOnCheck: [''],
        State: [''],
        InsuranceNumber: [''],
        BusinessLicenseNumber: [''],
        BLExpiryDate: [''],
        SSN: [''],
        DateOfBirth: ['']
      }
    );
  }

  ngOnInit(): void {
    this.getVendorCommissionUpdateStatus();
    this.getASCloudLicenseSettings();
    this.getVendorInfobyId();
    this.loadStates(1);
    this.loadDefaultPOSCommission();
    this.toggleControl('ApplyFlatCommissionPos', 'FlatCommissionPos');
    this.toggleControl('EnableConsignmentCommission', 'ConsignmentCommission');
  }

  getASCloudLicenseSettings(): void {
    this.generalService.getASCloudLicenseSettings().subscribe({
      next: (res: any) => {
        this.enableInventoryControl = res?.EnableInventoryControl ?? false;
      },
      error: () => {
        this.enableInventoryControl = false;
      }
    });
  }

  getVendorCommissionUpdateStatus(): void {
    this.vendorService
      .HasVendorTransactionsAfterLastPayout(this.vendorId)
      .subscribe({
        next: (res: any) => {
          this.hasTransactionsAfterLastPayout = res?.HasTransactionsAfterLastPayout ?? false;
          this.hasInventoryTransactions = res?.HasInventoryTransactions ?? false;
        },
        error: () => {
          this.hasTransactionsAfterLastPayout = false;
          this.hasInventoryTransactions = false;
        }
      });
  }

  getVendorSetting() {
    this.loaderIcon = Promise.resolve(true);
    this.subscriptions.push(this.vendorService.GetMarketVendorSettings(this.vendorId).subscribe((res: any) => {

      if (!res) {
        this.toastr.error($localize`Something went wrong`, $localize`Failed`, this.sharedHTMLSettings.ToasterConfig);
        this.loaderIcon = Promise.resolve(false);
        return;
      }
      this.OverrideCommissions = res.OverrideCommissions;
      this.originalFlatCommission = Number(res.FlatCommissionPos);
      this.vendorSettingsForm.patchValue(
        {
          VId: res.VendorId,
          ExcludeAutoMonthlyRenewal: res.ExcludeAutoMonthlyRenewal,
          AutoDeductRent: res.AutoDeductRent,
          RefundPosSalesTax: res.RefundPosSalesTax,
          DisablePrintSettlementCheckForDealer: res.DisablePrintSettlementCheckForDealer,
          ConsignmentVendor: res.ConsignmentVendor,
          ApplyFlatCommissionPos: res.ApplyFlatCommissionPos,
          EnableConsignmentCommission: res.EnableConsignmentCommission,
          FlatCommissionPos: (res.FlatCommissionPos).toFixed(2) ?? 0,
          ConsignmentCommission: (res.ConsignmentCommission).toFixed(2) ?? 0,
          VendorNameOnCheck: res.VendorNameOnCheck ?? "",
          VendorCityOnCheck: res.VendorCityOnCheck ?? "",
          VendorStateOnCheck: res.VendorStateOnCheck ?? "",
          MarketVendorId: res.MarketVendorId,
          InsuranceNumber: res.InsuranceNumber,
          BusinessLicenseNumber: res.BusinessLicenseNumber,
          BLExpiryDate: this.getValidDate(res.BLExpiryDate),
          SSN: res.SSN,
          DateOfBirth: this.getValidDate(res.DateOfBirth),
        }
      );

      setTimeout(() => {
        ['FlatCommissionPos', 'ConsignmentCommission'].forEach(controlName => {
          const input = document.querySelector<HTMLInputElement>(
            `input[formcontrolname="${controlName}"]`
          );

          input?.dispatchEvent(new FocusEvent('blur'));
        });
      });

      if (!this.vendorSettingsForm.get('ApplyFlatCommissionPos')?.value) {
        this.vendorSettingsForm.get('FlatCommissionPos')?.disable();
      }

      if (!this.vendorSettingsForm.get('EnableConsignmentCommission')?.value) {
        this.vendorSettingsForm.get('ConsignmentCommission')?.disable();
      }

      this.vendorSettingsForm.markAsPristine();
      this.vendorSettingsForm.markAsUntouched();

      this.loaderIcon = Promise.resolve(false);
    }));
  }

  getValidDate(date: any): Date | null {
    if (!date || date.includes('1900')) {
      return null;
    }

    const [year, month, day] = date.substring(0, 10).split('-').map(Number);

    return new Date(year, month - 1, day);
  }

  ToggleConsComm() {
    if (this.vendorSettingsForm.value['EnableConsignmentCommission'] && this.OverrideCommissions) {
      this.vendorSettingsForm.get('ConsignmentCommission')?.enable();
    } else {
      this.vendorSettingsForm.get('ConsignmentCommission')?.disable();
    }
  }

  ToggleFlatComm() {
    if (this.vendorSettingsForm.value['ApplyFlatCommissionPos'] && this.OverrideCommissions) {
      this.vendorSettingsForm.get('FlatCommissionPos')?.enable();
    } else {
      this.vendorSettingsForm.get('FlatCommissionPos')?.disable();
    }
  }

  toggleControl(toggleName: string, controlName: string) {
    const toggleControl = this.vendorSettingsForm.get(toggleName);
    const targetControl = this.vendorSettingsForm.get(controlName);

    const sub = toggleControl?.valueChanges.subscribe((checked: boolean) => {
      if (checked) {
        targetControl?.enable();
      } else {

        if (toggleName === 'ApplyFlatCommissionPos') {
          targetControl?.setValue(this.defaultFlatCommission);
        }
        else if (toggleName === 'EnableConsignmentCommission') {
          targetControl?.setValue(this.defaultConCommission);
        }

        targetControl?.disable();

        setTimeout(() => {
          const input = document.querySelector<HTMLInputElement>(
            `input[formControlName="${controlName}"]`
          );

          input?.dispatchEvent(new FocusEvent('blur'));
        });
      }
    });

    if (sub) {
      this.subscriptions.push(sub);
    }

    // Initial state
    if (!toggleControl?.value) {
      targetControl?.disable();
    }

  }

  UpdateVendorSetting(e: any): void {
    e.preventDefault();

    this.vendorSettingsForm.patchValue({
      VendorId: this.vendorId
    });

    const blExpiryControl = this.vendorSettingsForm.get('BLExpiryDate');
    const dobControl = this.vendorSettingsForm.get('DateOfBirth');

    const vendorSettings = {
      VendorId: this.vendorSettingsForm.get('VendorId')?.value,
      MarketVendorId: this.vendorSettingsForm.get('MarketVendorId')?.value,
      ExcludeAutoMonthlyRenewal: this.vendorSettingsForm.get('ExcludeAutoMonthlyRenewal')?.value,
      AutoDeductRent: this.vendorSettingsForm.get('AutoDeductRent')?.value,
      RefundPosSalesTax: this.vendorSettingsForm.get('RefundPosSalesTax')?.value,
      DisablePrintSettlementCheckForDealer: this.vendorSettingsForm.get('DisablePrintSettlementCheckForDealer')?.value,
      ConsignmentVendor: this.vendorSettingsForm.get('ConsignmentVendor')?.value,
      ApplyFlatCommissionPos: this.vendorSettingsForm.get('ApplyFlatCommissionPos')?.value,
      EnableConsignmentCommission: this.vendorSettingsForm.get('EnableConsignmentCommission')?.value,
      FlatCommissionPos: this.vendorSettingsForm.get('FlatCommissionPos')?.value,
      ConsignmentCommission: this.vendorSettingsForm.get('ConsignmentCommission')?.value,
      VendorNameOnCheck: this.vendorSettingsForm.get('VendorNameOnCheck')?.value,
      VendorCityOnCheck: this.vendorSettingsForm.get('VendorCityOnCheck')?.value,
      VendorStateOnCheck: this.vendorSettingsForm.get('VendorStateOnCheck')?.value,
      InsuranceNumber: this.vendorSettingsForm.get('InsuranceNumber')?.value,
      BusinessLicenseNumber: this.vendorSettingsForm.get('BusinessLicenseNumber')?.value,
      BLExpiryDate: blExpiryControl?.value ? this.datePipe.transform(blExpiryControl.value, 'yyyy-MM-dd') : null,
      SSN: this.vendorSettingsForm.get('SSN')?.value,
      DateOfBirth: dobControl?.value ? this.datePipe.transform(dobControl.value, 'yyyy-MM-dd') : null,
    }

    const updateModel = {
      VendorId: this.vendorId,
      FlatCommission: Number(this.vendorSettingsForm.get('FlatCommissionPos')?.value),
      UpdatePosInvoiceItems: false,
      UpdateInventoryItems: false
    };

    const flatCommissionChanged = this.originalFlatCommission !== Number(this.vendorSettingsForm.get('FlatCommissionPos')?.value);

    if (this.vendorSettingsForm.valid) {
      if (flatCommissionChanged) {

        if (this.hasTransactionsAfterLastPayout) {
          this.showFlatCommissionPopup(vendorSettings, updateModel);
        }
        else if (this.enableInventoryControl && this.hasInventoryTransactions) {
          this.showInventoryPopup(vendorSettings, updateModel);
        }
        else {
          this.saveVendorSettings(vendorSettings, updateModel);
        }
      } else {
        this.saveVendorSettings(vendorSettings, updateModel);
      }
    } else {
      this.toastr.error($localize`please check the form`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig)
      this.loaderIcon = Promise.resolve(false);
    }
  }

  private showFlatCommissionPopup(vendorSettings: any, updateModel: any): void {

    Swal.fire({
      title: 'Vendor',
      text: 'Do you want to change the Flat Commission for the current payout period?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No'
    }).then(result => {

      updateModel.UpdatePosInvoiceItems = result.isConfirmed;

      if (this.enableInventoryControl && this.hasInventoryTransactions) {
        this.showInventoryPopup(vendorSettings, updateModel);
      } else {
        this.saveVendorSettings(vendorSettings, updateModel);
      }
    });
  }

  private showInventoryPopup(vendorSettings: any, updateModel: any): void {

    Swal.fire({
      title: 'Vendor',
      text: 'Do you want to change the Flat Commission for the Existing Inventory Items of this Vendor?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No'
    }).then(result => {

      updateModel.UpdateInventoryItems = result.isConfirmed;
      this.saveVendorSettings(vendorSettings, updateModel);
    });
  }

  private saveVendorSettings(vendorSettings: any, updateModel: any): void {
    this.loaderIcon = Promise.resolve(true);
    this.subscriptions.push(
      this.vendorService.UpdateMarketVendorSettings(vendorSettings)
        .subscribe({
          next: (res: any) => {
            this.response = res;
            if (this.response.Success) {
              this.vendorSettingsForm.markAsPristine();
              this.toastr.success(this.response.Message, $localize`Success`, this.sharedHTMLSettings.ToasterConfig);

              if (updateModel.UpdatePosInvoiceItems || updateModel.UpdateInventoryItems) {
                this.updateFlatCommission(updateModel);
              }

              this.getVendorSetting();
            } else {
              this.toastr.error(this.response.Message, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
            }
            this.loaderIcon = Promise.resolve(false);
          },
          error: () => {
            this.toastr.error($localize`Something Went Wrong`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
            this.loaderIcon = Promise.resolve(false);
          }
        })
    );
  }

  private updateFlatCommission(updateModel: any): void {

    this.subscriptions.push(
      this.vendorService.UpdateVendorFlatCommission(updateModel)
        .subscribe({
          next: (res: any) => {

            if (res.Success) {
              this.toastr.success($localize`Flat commission updated successfully.`, $localize`Success`, this.sharedHTMLSettings.ToasterConfig);
            } else {
              this.toastr.error(res.Message, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
            }

          },
          error: () => {
            this.toastr.error($localize`Something Went Wrong`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
          }
        })
    );
  }

  CancelChanges() {
    this.vendorSettingsForm.markAsPristine();
    this.getVendorSetting()
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (!/[0-9.]|\b/.test(event.key)) {
      event.preventDefault();
      return;
    }

    if (event.key === '.' && value.includes('.')) {
      event.preventDefault();
      return;
    }

    const decimalIndex = value.indexOf('.');
    if (decimalIndex >= 0 && value.split('.')[1].length >= 3 && input.selectionStart! > decimalIndex) {
      event.preventDefault();
    }
  }

  fillZeroIfEmpty(controlName: string) {
    const control = this.vendorSettingsForm.get(controlName);
    if (!control) return;
    const value = control.value?.toString().trim();
    if (value === '') {
      control.setValue('0.00');
      return;
    }
    if (/\.\d+/.test(value)) {
      return;
    }
    if (!value.includes('.')) {
      control.setValue(value + '.00');
    }
    if (value.includes('.')) {
      control.setValue(value + '00');
    }
  }

  getVendorInfobyId() {
    this.vendorService.GetVendorById(this.vendorId).subscribe((response) => {
      if (response) {
        this.vendorInfo = response;
        this.vendorMarketID = this.vendorInfo.MarketVendorId;
        this.vendorName = this.vendorInfo.VendorName;
        this.cellPhone = this.vendorInfo.CellPhone;
        this.telephone = this.vendorInfo.Telephone;
        this.Email = this.vendorInfo.Email;
        this.Address = this.vendorInfo.Address;
        this.City = this.vendorInfo.City;
      }
    }, (err) => {
    });
  }

  loadStates(countryId: number): void {
    this.generalService.getStates(countryId)
      .subscribe({
        next: (res) => {
          this.statesList = res;

          this.getVendorSetting();
        },
        error: (err) => {
          console.error('Failed to load US States', err);
        }
      });
  }

  private loadDefaultPOSCommission(): void {

    this.subscriptions.push(
      this.posSettingsService.getSalesTaxAndCommission()
        .subscribe((res: any) => {
          this.defaultFlatCommission = Number(res?.Commission?.FlatCommission) || 0;
          this.defaultConCommission = Number(res?.Commission?.DealerConCommPercent) || 0;
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }
}
